import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/** Values below this (when read as milliseconds) are treated as Unix seconds — typical API mix-up. */
const EPOCH_SECONDS_VS_MS_THRESHOLD = 1e12;

function parseToValidDate(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const fromIso = parseISO(trimmed);
  if (isValid(fromIso)) {
    return fromIso;
  }

  // Some providers store `author.date` as a Unix timestamp *string* (seconds or ms). `parseISO`
  // and `new Date(string)` expect ISO-8601 (or other date grammar), not bare digits.
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      return undefined;
    }
    const ms = n < EPOCH_SECONDS_VS_MS_THRESHOLD ? n * 1000 : n;
    const d = new Date(ms);
    return isValid(d) ? d : undefined;
  }

  return undefined;
}

/**
 * `now - value` in milliseconds, or `undefined` when `value` is not a real
 * instant — several surfaces carry placeholder times (`'now'`, `'1h'`) that
 * must not be read as an age. Callers should treat `undefined` as "unknown",
 * never as zero. A `value` in the future yields a negative result.
 */
export function millisecondsSince(value: string, now: number): number | undefined {
  const date = parseToValidDate(value);
  return date ? now - date.getTime() : undefined;
}

/**
 * Prefer commit/VCS timestamps when they yield a real instant; otherwise try `fallback`.
 * Accepts ISO-8601 strings or digit-only Unix epoch strings (values below 1e12 → seconds, else ms).
 *
 * Returns a UTC ISO string, or `undefined` when neither input parses — callers should omit UI or
 * use another field instead of showing an arbitrary string.
 */
export function coalesceParsableIsoDate(
  preferred: string | undefined | null,
  fallback: string | undefined | null
): string | undefined {
  const trimmedPreferred = preferred?.trim();
  if (trimmedPreferred) {
    const d = parseToValidDate(trimmedPreferred);
    if (d) {
      return d.toISOString();
    }
  }
  const trimmedFallback = fallback?.trim();
  if (trimmedFallback) {
    const d = parseToValidDate(trimmedFallback);
    if (d) {
      return d.toISOString();
    }
  }
  return undefined;
}

// Date-only API fields ("2026-10-01") name a calendar day, not an instant: new Date() reads them
// as UTC midnight, which is the previous day west of UTC, so read them as local dates instead.
export const parseDateOnly = (dateString: string): Date => parseISO(dateString);

// "Sep 15 – Oct 15, 2026"; the start keeps its own year only when the range crosses one.
export function formatDateRange(start: Date, end: Date): string {
  const startPattern = start.getFullYear() === end.getFullYear() ? 'MMM d' : 'MMM d, yyyy';
  return `${format(start, startPattern)} – ${format(end, 'MMM d, yyyy')}`;
}

// UTC dates stored in state/URL need to be converted to local calendar dates for display.
// We compute the day offset between the browser's local "today" and UTC "today", then apply
// that offset to the UTC date so the calendar and labels show the correct local day.
// Math.round absorbs sub-day differences caused by DST transitions.
export function utcToLocalCalendarDate(utcDate: Date): Date {
  const now = new Date();
  const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const utcTodayAsLocal = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const dayOffset = Math.round((localToday.getTime() - utcTodayAsLocal.getTime()) / 86400000);
  return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate() + dayOffset);
}

export const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (isValid(date)) {
    const relativeDate = formatDistanceToNow(date, {
      addSuffix: true,
    });
    return relativeDate === 'less than a minute ago' ? 'just now' : `${relativeDate.replace(/^about /, '')}`;
  }
  return 'Unknown';
};

const fourWeeksInMs = 4 * 7 * 24 * 60 * 60 * 1000;

function formatSmartRelativeDistance(date: Date): string {
  const distance = formatDistanceToNow(date, { addSuffix: true });
  return distance
    .replace(/less than a minute ago/, 'Just now')
    .replace(/1m ago/, 'Just now')
    .replace(/(\d+)\s+(day|hour|week|month|year|minute)s?/g, (_, num, unit) => `${num}${unit[0]}`)
    .replace(/about /, '')
    .replace(/less than /, '<')
    .replace(/over /, '>')
    .replace(/almost /, '~');
}

interface SmartRelativeDateOptions {
  /** Leave the year off dates in the current year ("Aug 31" rather than "Aug 31, 2026"). */
  omitCurrentYear?: boolean;
}

export const formatSmartRelativeDate = (dateString: string, options: SmartRelativeDateOptions = {}): string => {
  const date = parseISO(dateString);
  const now = new Date();

  if (now.getTime() - date.getTime() > fourWeeksInMs) {
    const showsYear = !(options.omitCurrentYear && date.getFullYear() === now.getFullYear());
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(showsYear ? { year: 'numeric' } : {}),
    });
  }

  return formatSmartRelativeDistance(date);
};

/** Same as formatSmartRelativeDate but omits year for dates older than four weeks (dense lists). */
export const formatSmartRelativeDateCompact = (dateString: string): string => {
  const date = parseISO(dateString);
  const now = new Date();

  if (now.getTime() - date.getTime() > fourWeeksInMs) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  return formatSmartRelativeDistance(date);
};

/**
 * Compact "last commit" label for branch lists: prefers the smart-relative compact form, falling
 * back to a plain relative distance, and degrading to an em dash when the input can't be parsed.
 */
export function formatLastCommit(dateString: string): string {
  try {
    const compact = formatSmartRelativeDateCompact(dateString);
    if (compact) return compact;
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return '—';
  }
}
